// components/PhoneEmailVerify.jsx
//
// Wraps the phone.email widget (the snippet you pasted) as a proper
// React component so it can be dropped into Login.jsx and Signup.jsx.
//
// How it works:
// 1. On mount, we inject phone.email's script tag into the page
// 2. phone.email requires a GLOBAL function called `phoneEmailReceiver` -
//    it's not a React prop, it's a plain window-level function it calls
//    directly, so we define it on `window` here
// 3. When the user verifies their email, phone.email calls that function
//    with a `user_json_url` - we send ONLY that URL to our backend
//    (never trust the email address if it were sent directly from the
//    browser - always verify server-side, which is what our
//    /api/auth/phone-email-verify route does)
// 4. Backend responds with a normal login token (same shape as regular
//    login/signup) - we hand that back to the parent page via onVerified
//
// IMPORTANT: replace CLIENT_ID below with your real phone.email client
// ID from your phone.email dashboard.

import { useEffect } from 'react';

const CLIENT_ID = 'YOUR_CLIENT_ID'; // <-- replace with your real phone.email client ID
import { API_BASE } from '../config';

function PhoneEmailVerify({ onVerified, onError, rawMode = false }) {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.phone.email/verify_email_v1.js';
    script.async = true;
    document.body.appendChild(script);

    // phone.email calls this exact function name when verification succeeds
    window.phoneEmailReceiver = async (userObj) => {
      // rawMode: used by the "forgot password" flow - it needs the
      // verification URL itself (to pair with a new password the user
      // sets afterward), NOT an immediate login. Skips the backend call
      // entirely here; the parent component decides what to do with it.
      if (rawMode) {
        onVerified({ user_json_url: userObj.user_json_url });
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/auth/phone-email-verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_json_url: userObj.user_json_url }),
        });

        const data = await res.json();

        if (!res.ok) {
          onError?.(data.message || 'Email verification failed.');
          return;
        }

        onVerified(data);
      } catch (err) {
        onError?.('Could not verify email. Please try again.');
      }
    };

    // Cleanup when the component using this unmounts - removes the
    // script tag and the global function so it doesn't leak between pages
    return () => {
      document.body.removeChild(script);
      delete window.phoneEmailReceiver;
    };
  }, [onVerified, onError, rawMode]);

  return <div className="pe_verify_email" data-client-id={CLIENT_ID}></div>;
}

export default PhoneEmailVerify;
